/**
 * One simulation tick, in the one order everything must take it.
 *
 * This exists because there were two copies of it — the game's loop in `main.ts` and the
 * measurement bench's in `tools/measure/headless.ts` — and they had already drifted apart. The
 * bench skipped `finishStep` on the tick a match ended, so the last tick of every measured match
 * had no collisions, no fatigue drain, no referee and no recorded sample, while the same tick in
 * a real match had all of them. A bench that plays a slightly different game than the game is
 * worse than no bench, because every number it produces still looks reasonable.
 *
 * The order matters and is the original's (`Match::Process`, match.cpp:800-1133):
 *
 *  1. The match steps: referee, ball, team shape, who is going for the ball.
 *  2. Every player animates, and is POSED as he goes. The pose cannot wait for drawing time:
 *     the tackle collision below reads the boxes around his arms and legs, and a frame may
 *     contain several ticks (`match.cpp:1836-1922`).
 *  3. A player carrying the ball in his hands puts it where his hands are, and the ball's
 *     three-second forecast is rebuilt around that.
 *  4. `finishStep` closes the tick: fatigue, collisions, the referee's view of them, and the
 *     recording's sample — which is taken as its own last act and nowhere else.
 *  5. The three officials walk, last of all. They are in none of the football above — see
 *     `src/sim/officials.ts` — and they are here rather than in the caller for one reason:
 *     the referee's judgment now answers to where his body is, so a bench that did not walk
 *     him would referee a different match from the game. That is the exact fault this file
 *     was written to make impossible.
 */

import { Vector3 } from 'three';
import type { Ball } from './ball';
import type { Match, MatchPlayer } from './match';
import type { PlayerCommand } from './anim/select';
import type { HumanoidAnim } from './anim/runtime';
import type { Skeleton } from './skeleton';
import { tickOfficials, type Official } from './officials';

/** What one tick needs from a player: the man, the clip he is playing, and his bones. */
export interface SteppedPlayer {
  player: MatchPlayer;
  anim: HumanoidAnim;
  skeleton: Skeleton;
}

export interface StepOptions {
  match: Match;
  ball: Ball;
  players: readonly SteppedPlayer[];
  /**
   * The referee and the two linesmen, when they have bodies.
   *
   * Omitted leaves the referee omniscient, which is what every match without officials in it
   * has always been and what the rules tests rely on (`src/sim/referee.ts`, `viewpoints`).
   */
  officials?: readonly Official[];
  /** What this player is being told to do this tick. */
  commands: (player: MatchPlayer) => PlayerCommand[];
  /**
   * Run after the match has stepped and before any player animates.
   *
   * The one seam a caller needs: the game reads its devices here, because a person's orders
   * have to be in hand before the man he is driving picks a clip.
   */
  afterStep?: () => void;
}

const _heldBall = new Vector3();

export function stepMatch(options: StepOptions): void {
  const { match, ball, players, commands } = options;

  match.step();
  options.afterStep?.();

  for (const entry of players) {
    // A dismissed man is not skipped outright: `isDeparting` keeps him ticking — and so
    // drawable — for a bounded window after `Match.sendOff`, walking for the touchline instead
    // of freezing on the spot. See `MatchPlayer.isDeparting`.
    if (!entry.player.active && !entry.player.isDeparting(match.timeMs)) continue;
    entry.anim.tick(match, entry.player, () => commands(entry.player));
    entry.anim.writePose(entry.skeleton);
    entry.skeleton.update();
    if (match.retainedBallPlayer === entry.player) {
      const position = entry.anim.retainedBallPosition(entry.skeleton, _heldBall);
      if (position) {
        ball.setPosition(position);
        match.refreshBallPredictions();
      }
    }
  }

  // Unconditional, including on the tick the final whistle goes. Everything a match counts is
  // a difference between two ticks, so a tick that never closes loses whatever happened on it
  // and says nothing about having done so.
  match.finishStep();

  // AFTER `finishStep`, which is where the collisions this tick's fouls come out of are run,
  // so the position the referee judges from is the one he had at the end of the tick before —
  // ten milliseconds stale, which is nothing, and the same ten milliseconds the game already
  // had before this call was moved in here from `main.ts`.
  if (options.officials) tickOfficials(match, options.officials);
}
